import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Check, ExternalLink } from "lucide-react";
import { sendMagicLink, supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/useSession";
import { posthog } from "@/lib/posthog";
import { Logo } from "@/components/Logo";

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>) => {
    const result: { next?: string; email?: string; sent?: string; context?: string } = {};
    if (typeof search.next === "string") result.next = search.next;
    if (typeof search.email === "string") result.email = search.email;
    if (typeof search.sent === "string") result.sent = search.sent;
    if (search.context === "listing" || search.context === "alert" || search.context === "search") result.context = search.context as string;
    return result;
  },
  head: () => ({
    meta: [
      { title: "Entrar — Busca Blindados" },
      { name: "description", content: "Acesse seus alertas de carros blindados." },
    ],
  }),
  component: LoginPage,
});

function LoginPage() {
  const { session, loading } = useSession();
  const { next, email: searchEmail, sent: searchSent, context } = Route.useSearch();
  const fromListing = context === "listing";
  const fromAlert = context === "alert";
  const fromSearch = context === "search";
  const navigate = useNavigate();

  const [email, setEmail] = useState(searchEmail ?? "");
  const [sent, setSent] = useState(searchSent === "1");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noAccount, setNoAccount] = useState(false);

  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);

  const [passwordMode, setPasswordMode] = useState(false);
  const [password, setPassword] = useState("");
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    if (!loading && session) navigate({ to: "/alertas" });
  }, [session, loading, navigate]);

  useEffect(() => {
    if (sent) codeRef.current?.focus();
  }, [sent]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitLoading(true);
    setError(null);
    setNoAccount(false);
    try {
      const redirectTo = next
        ? `${window.location.origin}/auth/callback?next=${encodeURIComponent(next)}`
        : undefined;
      await sendMagicLink(email.trim(), { redirectTo, createUser: !!next });
      posthog.capture("magic_link_requested", { $set_once: { email: email.trim() } });
      setSent(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.toLowerCase().includes("signups not allowed")) {
        setNoAccount(true);
        posthog.capture("login_no_account_shown");
      } else {
        setError(msg || "Algo deu errado.");
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setVerifying(true);
    setError(null);
    try {
      const { error: otpError } = await supabase.auth.verifyOtp({
        email: email.trim(),
        token: code.trim(),
        type: "email",
      });
      if (otpError) throw otpError;
      posthog.capture("otp_verified");
      navigate({ to: next ?? "/alertas" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Código inválido ou expirado.");
      setCode("");
      codeRef.current?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const signInWithGoogle = async () => {
    setError(null);
    const redirectTo = next
      ? `${window.location.origin}/auth/callback?next=${encodeURIComponent(next)}`
      : `${window.location.origin}/auth/callback`;
    const { error: oauthErr } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo },
    });
    if (oauthErr) setError(oauthErr.message ?? "Falha no login com Google.");
  };

  const signInWithPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setPwLoading(true);
    setError(null);
    const { error: pwErr } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (pwErr) {
      setError(
        pwErr.message?.toLowerCase().includes("invalid")
          ? "Email ou senha inválidos."
          : pwErr.message ?? "Não foi possível entrar.",
      );
      setPwLoading(false);
      return;
    }
    posthog.capture("password_login");
    navigate({ to: next ?? "/alertas" });
  };

  const resend = async () => {
    setError(null);
    setCode("");
    try {
      const redirectTo = next
        ? `${window.location.origin}/auth/callback?next=${encodeURIComponent(next)}`
        : undefined;
      await sendMagicLink(email.trim(), { redirectTo, createUser: !!next });
    } catch {
      setError("Não foi possível reenviar. Tente novamente.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen text-foreground flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando sessão...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-foreground">
      <nav className="mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
        <button
          type="button"
          onClick={() => {
            // Prefer browser history so we return to /buscar with filters
            // intact (or wherever the user was). Fallback to home for direct
            // entries (history.length === 1 on a fresh tab).
            if (typeof window !== "undefined" && window.history.length > 1) {
              window.history.back();
            } else {
              navigate({ to: "/" });
            }
          }}
          className="chip inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-foreground/80 transition dark:hover:bg-white/10 hover:bg-black/10"
          aria-label="Voltar"
        >
          <span aria-hidden>←</span> Voltar
        </button>
        <Link to="/" className="text-foreground" aria-label="Busca Blindados">
          <Logo className="h-6 w-auto md:h-10" />
        </Link>
      </nav>

      <main className="mx-auto max-w-md px-6 pt-16 sm:pt-24">
        <div className="surface-card rounded-3xl px-6 py-10 sm:px-10">
          {!sent ? (
            <>
              {fromListing ? (
                <div className="text-center">
                  <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full border border-primary/40 bg-primary/10 text-primary">
                    <ExternalLink className="h-5 w-5" />
                  </div>
                  <h1 className="font-display text-2xl text-foreground sm:text-3xl">
                    Pra ver esse anúncio, crie sua conta grátis
                  </h1>
                  <p className="mx-auto mt-3 max-w-sm text-sm text-muted-foreground">
                    É em segundos — Google, senha ou link no email. Você volta direto pra busca depois.
                  </p>
                  <div className="mx-auto mt-5 flex max-w-xs flex-col gap-2 text-left text-xs text-muted-foreground">
                    <Perk>Gratuito · sem cartão de crédito</Perk>
                    <Perk>Acesso ao link de todos os anúncios</Perk>
                    <Perk>1 alerta grátis — a gente te avisa quando aparecer um match</Perk>
                  </div>
                </div>
              ) : fromSearch ? (
                <div className="text-center">
                  <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full border border-primary/40 bg-primary/10 text-primary">
                    <Check className="h-5 w-5" />
                  </div>
                  <h1 className="font-display text-2xl text-foreground sm:text-3xl">
                    Crie sua conta grátis para ver mais resultados
                  </h1>
                  <p className="mx-auto mt-3 max-w-sm text-sm text-muted-foreground">
                    É em segundos — Google, senha ou link no email. Você volta direto pra busca depois.
                  </p>
                  <div className="mx-auto mt-5 flex max-w-xs flex-col gap-2 text-left text-xs text-muted-foreground">
                    <Perk>Gratuito · sem cartão de crédito</Perk>
                    <Perk>Acesso a todos os resultados da busca</Perk>
                    <Perk>1 alerta grátis — a gente te avisa quando aparecer um match</Perk>
                  </div>
                </div>
              ) : fromAlert ? (
                <div className="text-center">
                  <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full border border-primary/40 bg-primary/10 text-primary">
                    <Check className="h-5 w-5" />
                  </div>
                  <h1 className="font-display text-2xl text-foreground sm:text-3xl">
                    Para criar alertas, crie sua conta grátis
                  </h1>
                  <p className="mx-auto mt-3 max-w-sm text-sm text-muted-foreground">
                    É em segundos — Google, senha ou link no email.
                  </p>
                  <div className="mx-auto mt-5 flex max-w-xs flex-col gap-2 text-left text-xs text-muted-foreground">
                    <Perk>Gratuito · sem cartão de crédito</Perk>
                    <Perk>1 alerta grátis — a gente te avisa por email</Perk>
                    <Perk>Acesso ao link de todos os anúncios</Perk>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <h1 className="font-display text-3xl text-foreground">Entrar</h1>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Acesse com Google, senha ou link mágico no email.
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={signInWithGoogle}
                className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full border dark:border-white/10 border-black/10 dark:bg-white/[0.04] bg-black/[0.04] px-5 py-3 text-sm font-medium text-foreground transition dark:hover:bg-white/[0.08] hover:bg-black/[0.08]"
              >
                <GoogleIcon className="h-4 w-4" />
                Continuar com Google
              </button>

              <div className="my-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                <div className="h-px flex-1 dark:bg-white/10 bg-black/10" />
                ou
                <div className="h-px flex-1 dark:bg-white/10 bg-black/10" />
              </div>

              <form
                onSubmit={passwordMode ? signInWithPassword : submit}
                className="space-y-4 text-left"
              >
                <label className="block">
                  <span className="mb-2 block text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                    Email
                  </span>
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="voce@email.com"
                    className="w-full rounded-lg border dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] px-3 py-2.5 text-sm text-foreground outline-none transition focus:border-primary/60 dark:focus:bg-white/[0.05] focus:bg-black/[0.05]"
                  />
                </label>

                {passwordMode && (
                  <label className="block">
                    <span className="mb-2 block text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                      Senha
                    </span>
                    <input
                      type="password"
                      required
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full rounded-lg border dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] px-3 py-2.5 text-sm text-foreground outline-none transition focus:border-primary/60 dark:focus:bg-white/[0.05] focus:bg-black/[0.05]"
                    />
                  </label>
                )}

                {noAccount && (
                  <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-3 text-sm text-amber-400">
                    <p>Não encontramos uma conta com este email. Ainda não tem conta?</p>
                    <a
                      href="/#criar-alerta"
                      onClick={() => posthog.capture("login_no_account_cta_clicked")}
                      className="btn-primary mt-3 inline-flex rounded-full px-4 py-2 text-xs font-medium"
                    >
                      Criar meu primeiro alerta grátis →
                    </a>
                  </div>
                )}
                {error && (
                  <p className="rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-400">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={!email || submitLoading || pwLoading || (passwordMode && !password)}
                  className="btn-primary w-full rounded-full px-5 py-3 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {passwordMode
                    ? pwLoading ? "Entrando..." : "Entrar com senha"
                    : submitLoading ? "Enviando..." : "Enviar link de acesso"}
                </button>
                <button
                  type="button"
                  onClick={() => { setPasswordMode((v) => !v); setError(null); setPassword(""); }}
                  className="block w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {passwordMode ? "Prefiro entrar pelo link mágico" : "Tenho senha — entrar direto"}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-primary/40 bg-primary/10 text-primary">
                  ✓
                </div>
                <h1 className="font-display text-2xl text-foreground">Verifique seu email</h1>
                <p className="mt-3 text-sm text-muted-foreground">
                  Clique no link que enviamos para{" "}
                  <span className="text-foreground">{email}</span>{" "}
                  ou digite o código abaixo.
                </p>
              </div>

              <form onSubmit={verifyCode} className="mt-8 space-y-4">
                <label className="block">
                  <span className="mb-2 block text-center text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                    Código de acesso
                  </span>
                  <input
                    ref={codeRef}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="000000"
                    className="w-full rounded-lg border dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] px-3 py-3 text-center text-2xl tracking-[0.5em] text-foreground outline-none transition focus:border-primary/60 dark:focus:bg-white/[0.05] focus:bg-black/[0.05]"
                  />
                </label>
                {error && (
                  <p className="rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-400 text-center">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={code.length !== 6 || verifying}
                  className="btn-primary w-full rounded-full px-5 py-3 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {verifying ? "Verificando..." : "Entrar com código"}
                </button>
              </form>

              <p className="mt-6 text-center text-xs text-muted-foreground">
                Não recebeu?{" "}
                <button
                  onClick={resend}
                  className="text-foreground/70 underline underline-offset-2 hover:text-foreground transition-colors"
                >
                  Reenviar
                </button>
                {" "}ou{" "}
                <button
                  onClick={() => { setSent(false); setError(null); setCode(""); }}
                  className="text-foreground/70 underline underline-offset-2 hover:text-foreground transition-colors"
                >
                  trocar email
                </button>
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

function Perk({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex items-start gap-2">
      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
      <span>{children}</span>
    </span>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path
        fill="#4285F4"
        d="M22.5 12.27c0-.79-.07-1.55-.2-2.27H12v4.51h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.09-1.92 3.22-4.74 3.22-8.3z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.12c-.22-.66-.35-1.36-.35-2.12s.13-1.46.35-2.12V7.04H2.18A10.99 10.99 0 0 0 1 12c0 1.78.43 3.46 1.18 4.96l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"
      />
    </svg>
  );
}
